/**
 * Brochure Builder — rep-facing generator page.
 *
 * Workflow:
 *   1) Rep pastes a vehicle listing URL (must be on an allowed dealer domain).
 *   2) Preview scrapes it → shows editable fields + photo list. Missing
 *      fields are flagged in `needs_manual`.
 *   3) Rep tweaks / fills required fields, hits Generate.
 *   4) Server returns a shareable `/brochure/<token>` link. Rep copies it
 *      and drops it into text/email.
 *   5) Below the generator: "My Brochures" — recent brochures with their
 *      engagement counts (opened / call / text / credit app).
 *
 * Route: sidebar nav id `brochure-builder`, gated by
 * `user.brochure_builder_enabled` on the /me response.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import {
  Loader2, Link2, Copy, Wand2, ExternalLink, RefreshCw, Camera,
  Phone, User as UserIcon, AlertTriangle, CheckCircle2, Eye, Trash2,
  Search, X as XIcon, Trophy,
} from "lucide-react";

const INPUT_CLS =
  "w-full rounded-md border border-gray-200 px-3 py-2 text-[13px] bg-white " +
  "focus:outline-none focus:ring-2 focus:ring-gray-300";

const REQUIRED_FIELDS = ["year", "make", "model", "price", "mileage"];
const OPTIONAL_FIELDS = [
  "vin", "trim", "body_style", "transmission",
  "drivetrain", "exterior_color", "interior_color",
];
const FIELD_LABELS = {
  year: "Year", make: "Make", model: "Model", trim: "Trim",
  price: "Price (USD)", mileage: "Mileage",
  vin: "VIN", body_style: "Body style", transmission: "Transmission",
  drivetrain: "Drivetrain", exterior_color: "Exterior color",
  interior_color: "Interior color", title: "Title",
};

function shortDate(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
}

function absShareUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  if (typeof window === "undefined") return pathOrUrl;
  return window.location.origin + pathOrUrl;
}

// ─────────────────── Rep profile card ───────────────────

function RepProfileCard({ status, onSaved }) {
  const { apiFetch } = useAuth();
  const { success: toastOk, error: toastErr } = useToast();
  const rep = status?.rep || {};
  const [phone, setPhone] = useState(rep.phone || "");
  const [photoUrl, setPhotoUrl] = useState(rep.photo_url || "");
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPhone(rep.phone || "");
    setPhotoUrl(rep.photo_url || "");
  }, [rep.phone, rep.photo_url]);

  const isDirty = (phone || "") !== (rep.phone || "");

  const save = async () => {
    setSaving(true);
    try {
      const r = await apiFetch("/dealer/brochure/me",
        { method: "PUT", data: { phone } });
      toastOk("Rep profile saved.");
      onSaved?.(r?.data || r);
    } catch (e) {
      toastErr(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5_000_000) {
      toastErr("Photo must be under 5MB.");
      return;
    }
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", f);
      const r = await apiFetch("/dealer/brochure/me/photo",
        { method: "POST", data: formData });
      const updated = r?.data || r;
      // Cache-bust so the <img> doesn't keep showing a stale cached photo
      // at the same URL right after a re-upload.
      const busted = `${updated.photo_url}?v=${Date.now()}`;
      setPhotoUrl(busted);
      toastOk("Photo uploaded.");
      onSaved?.({ ...updated, photo_url: busted });
    } catch (e2) {
      toastErr(e2?.response?.data?.detail || "Photo upload failed.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const clearPhoto = async () => {
    setPhotoUrl("");
    try {
      const r = await apiFetch("/dealer/brochure/me", { method: "PUT", data: { photo_url: "" } });
      onSaved?.(r?.data || r);
    } catch (e) {
      toastErr(e?.response?.data?.detail || "Couldn't remove photo.");
    }
  };

  const complete = !!phone;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5"
         data-testid="brochure-rep-profile-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-[15px] font-semibold text-gray-900 flex items-center gap-2">
            <UserIcon size={16} /> Your rep profile
          </h3>
          <p className="text-[12px] text-gray-500 mt-1 max-w-xl">
            Your phone number and photo appear on every brochure you generate.
            Customers tap-to-call and tap-to-text you directly from the page.
          </p>
        </div>
        <span
          className={`shrink-0 text-[11px] font-semibold px-2 py-1 rounded-full ${
            complete ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
          }`}
          data-testid="brochure-rep-profile-status"
        >
          {complete ? "Ready" : "Add phone to activate"}
        </span>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mt-4">
        <div className="flex flex-col items-center gap-2 md:w-[120px] shrink-0">
          <div
            className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 grid place-items-center border border-gray-200"
            data-testid="brochure-rep-photo-preview"
          >
            {photoUrl ? (
              <img src={photoUrl} alt="Rep" className="w-full h-full object-cover" />
            ) : (
              <UserIcon size={30} className="text-gray-400" />
            )}
          </div>
          <div className="flex flex-col gap-1 w-full">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="inline-flex items-center justify-center gap-1 text-[11.5px] font-semibold px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
              data-testid="brochure-rep-photo-pick"
            >
              {uploadingPhoto
                ? <Loader2 size={12} className="animate-spin" />
                : <Camera size={12} />}
              {uploadingPhoto ? "Uploading…" : "Upload"}
            </button>
            {photoUrl && (
              <button
                type="button"
                onClick={clearPhoto}
                className="inline-flex items-center justify-center gap-1 text-[11.5px] font-semibold px-2 py-1 rounded border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                data-testid="brochure-rep-photo-clear"
              >
                <Trash2 size={12} /> Remove
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onPickFile}
              className="hidden"
              data-testid="brochure-rep-photo-file"
            />
          </div>
        </div>

        <div className="space-y-3 flex-1 min-w-0">
          <label className="block">
            <span className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
              Cell / SMS number
            </span>
            <div className="relative">
              <Phone size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
              <input
                className={INPUT_CLS + " pl-8"}
                data-testid="brochure-rep-phone"
                placeholder="+1 555 123 4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <span className="block text-[11px] text-gray-400 mt-1">
              Used for both click-to-call and click-to-text on the brochure.
            </span>
          </label>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={save}
              disabled={saving || !isDirty}
              className="inline-flex items-center gap-2 rounded-md bg-gray-900 text-white text-[13px] font-semibold px-4 py-2 hover:bg-gray-800 disabled:opacity-50"
              data-testid="brochure-rep-save"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Save profile
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────── Generator card ───────────────────

function GeneratorCard({ status, onCreated }) {
  const { apiFetch } = useAuth();
  const { success: toastOk, error: toastErr } = useToast();

  const [url, setUrl] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);  // response from /preview
  const [fields, setFields] = useState({});      // rep-editable values
  const [photos, setPhotos] = useState([]);
  const [features, setFeatures] = useState([]);
  const [badges, setBadges] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);

  const allowedList = status?.allowed_domains || [];

  const resetForm = () => {
    setPreview(null);
    setFields({});
    setPhotos([]);
    setFeatures([]);
    setBadges([]);
    setLastCreated(null);
  };

  const runPreview = async () => {
    const trimmed = url.trim();
    if (!/^https?:\/\//i.test(trimmed)) {
      toastErr("Enter a full URL that starts with http:// or https://");
      return;
    }
    setPreviewing(true);
    setLastCreated(null);
    try {
      const r = await apiFetch("/dealer/brochure/preview",
        { method: "POST", data: { url: trimmed } });
      const p = r?.data || r;
      setPreview(p);
      setFields({ ...(p.fields || {}) });
      setPhotos(Array.isArray(p.photos) ? p.photos : []);
      setFeatures(Array.isArray(p.features) ? p.features : []);
      setBadges(Array.isArray(p.badges) ? p.badges : []);
      if (p.manual_only) {
        toastErr("Couldn't reach that page — fill everything in manually below.");
      } else if ((p.needs_manual || []).length > 0) {
        toastOk("Scraped what we could. Fill in the highlighted fields.");
      } else {
        toastOk("Scrape looks clean. Review, then Generate.");
      }
    } catch (e) {
      toastErr(e?.response?.data?.detail || "Preview failed.");
    } finally {
      setPreviewing(false);
    }
  };

  const setField = (k, v) => setFields((prev) => ({ ...prev, [k]: v }));

  const removePhoto = (idx) =>
    setPhotos((prev) => prev.filter((_, i) => i !== idx));

  const addPhotoUrl = () => {
    const u = window.prompt("Paste a photo URL");
    if (!u) return;
    const t = u.trim();
    if (!/^https?:\/\//i.test(t)) {
      toastErr("Photo URL must start with http:// or https://.");
      return;
    }
    setPhotos((prev) => [...prev, t]);
  };

  const missingRequired = useMemo(
    () => REQUIRED_FIELDS.filter((k) => !fields[k]),
    [fields]
  );

  const generate = async () => {
    if (missingRequired.length > 0) {
      toastErr(
        "Missing required fields: " +
        missingRequired.map((k) => FIELD_LABELS[k] || k).join(", ")
      );
      return;
    }
    setGenerating(true);
    try {
      const data = { ...fields, photos, features, badges };
      // Auto-title if the rep didn't type one.
      if (!data.title) {
        data.title = [data.year, data.make, data.model, data.trim]
          .filter(Boolean).join(" ").trim();
      }
      const r = await apiFetch("/dealer/brochure/generate", {
        method: "POST",
        data: {
          source_url: url.trim(),
          data,
          source_data: preview || {},
        },
      });
      const created = r?.data || r;
      setLastCreated(created);
      toastOk("Brochure created — copy the link below.");
      onCreated?.(created);
    } catch (e) {
      toastErr(e?.response?.data?.detail || "Generate failed.");
    } finally {
      setGenerating(false);
    }
  };

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toastOk("Link copied to clipboard.");
    } catch {
      toastErr("Couldn't copy — select and copy manually.");
    }
  };

  const needsManual = new Set(preview?.needs_manual || []);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4"
         data-testid="brochure-generator-card">
      <div>
        <h3 className="text-[15px] font-semibold text-gray-900 flex items-center gap-2">
          <Wand2 size={16} /> Build a new brochure
        </h3>
        <p className="text-[12px] text-gray-500 mt-1 max-w-2xl">
          Paste a listing URL from your dealership site. We&apos;ll pull vehicle
          info, price, and photos. You confirm/edit, then get a shareable link
          to text or email.
        </p>
      </div>

      {/* URL bar */}
      <div className="flex flex-col md:flex-row md:items-end gap-2">
        <label className="block flex-1">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
            Listing URL
          </span>
          <div className="relative">
            <Link2 size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
            <input
              className={INPUT_CLS + " pl-8"}
              data-testid="brochure-source-url"
              placeholder="https://your-inventory-site.com/vehicle/..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") runPreview(); }}
            />
          </div>
          {allowedList.length > 0 && (
            <span className="block text-[11px] text-gray-400 mt-1">
              Allowed hosts:{" "}
              <span className="text-gray-600">{allowedList.join(", ")}</span>
            </span>
          )}
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={runPreview}
            disabled={previewing || !url.trim()}
            className="inline-flex items-center gap-2 rounded-md bg-gray-900 text-white text-[13px] font-semibold px-4 py-2 hover:bg-gray-800 disabled:opacity-50"
            data-testid="brochure-preview-btn"
          >
            {previewing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Preview
          </button>
          {preview && (
            <button
              type="button"
              onClick={resetForm}
              className="rounded-md border border-gray-300 bg-white text-[13px] font-semibold px-3 py-2 hover:bg-gray-50"
              data-testid="brochure-reset-btn"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Warnings / manual notice */}
      {preview && (preview.warnings || []).length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900 flex items-start gap-2"
             data-testid="brochure-preview-warnings">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <ul className="space-y-0.5">
            {preview.warnings.map((w, i) => (<li key={i}>{w}</li>))}
          </ul>
        </div>
      )}

      {/* Editable field grid */}
      {preview && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {REQUIRED_FIELDS.concat(OPTIONAL_FIELDS).map((k) => (
              <label key={k} className="block">
                <span className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
                  {FIELD_LABELS[k] || k}
                  {REQUIRED_FIELDS.includes(k) && (
                    <span className="text-rose-500 normal-case tracking-normal">
                      *required
                    </span>
                  )}
                  {needsManual.has(k) && (
                    <span className="text-amber-600 normal-case tracking-normal">
                      needs manual
                    </span>
                  )}
                </span>
                <input
                  className={INPUT_CLS}
                  value={fields[k] == null ? "" : String(fields[k])}
                  onChange={(e) => setField(k, e.target.value)}
                  data-testid={`brochure-field-${k}`}
                />
              </label>
            ))}
          </div>

          {/* Photos manager */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                Photos ({photos.length})
              </span>
              <button
                type="button"
                onClick={addPhotoUrl}
                className="text-[11.5px] font-semibold text-blue-700 hover:underline"
                data-testid="brochure-photo-add"
              >
                + Add photo URL
              </button>
            </div>
            {photos.length === 0 ? (
              <div className="text-[12px] text-gray-400 border border-dashed border-gray-200 rounded-md p-4 text-center"
                   data-testid="brochure-photo-empty">
                No photos yet. Add at least one for a strong brochure.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2"
                   data-testid="brochure-photo-grid">
                {photos.map((p, i) => (
                  <div key={p + i} className="relative group border border-gray-200 rounded-md overflow-hidden bg-gray-50">
                    <img src={p} alt={`Photo ${i + 1}`}
                         className="w-full h-24 object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(i)}
                      className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-6 h-6 grid place-items-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`brochure-photo-remove-${i}`}
                      aria-label={`Remove photo ${i + 1}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Generate button + link result */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-2 border-t border-gray-100">
            <div className="text-[12px] text-gray-500">
              {missingRequired.length === 0
                ? <span className="text-emerald-700">Ready to generate.</span>
                : <>Fill required fields: <strong>
                    {missingRequired.map((k) => FIELD_LABELS[k] || k).join(", ")}
                  </strong></>}
            </div>
            <button
              type="button"
              onClick={generate}
              disabled={generating || missingRequired.length > 0}
              className="inline-flex items-center gap-2 rounded-md bg-emerald-600 text-white text-[13px] font-semibold px-5 py-2 hover:bg-emerald-700 disabled:opacity-50"
              data-testid="brochure-generate-btn"
            >
              {generating ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
              Generate brochure link
            </button>
          </div>

          {lastCreated && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2"
                 data-testid="brochure-last-created">
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-800">
                  Brochure ready
                </div>
                <div className="text-[13px] font-mono text-gray-900 truncate">
                  {absShareUrl(lastCreated.share_url)}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => copyLink(absShareUrl(lastCreated.share_url))}
                  className="inline-flex items-center gap-1 text-[12px] font-semibold px-3 py-1.5 rounded border border-emerald-300 bg-white text-emerald-900 hover:bg-emerald-100"
                  data-testid="brochure-copy-link"
                >
                  <Copy size={12} /> Copy
                </button>
                <a
                  href={absShareUrl(lastCreated.share_url)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[12px] font-semibold px-3 py-1.5 rounded border border-emerald-300 bg-white text-emerald-900 hover:bg-emerald-100"
                  data-testid="brochure-open-link"
                >
                  <ExternalLink size={12} /> Open
                </a>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────── My Brochures list ───────────────────

const DAYS_OPTIONS = [
  { value: 0, label: "All time" },
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

const EVENT_LABELS = {
  opened: "Opened",
  call_clicked: "Tapped to call",
  text_clicked: "Tapped to text",
  credit_app_clicked: "Tapped credit app",
};

function BrochureEventsDrawer({ brochureId, onClose }) {
  const { apiFetch } = useAuth();
  const { error: toastErr } = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  // Keep onClose in a ref so effects don't re-run when the parent
  // passes a fresh arrow reference on every render.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const r = await apiFetch(`/dealer/brochure/${brochureId}/events`,
          { method: "GET" });
        if (alive) setData(r?.data || r);
      } catch (e) {
        toastErr(e?.response?.data?.detail || "Could not load events.");
        if (alive) onCloseRef.current?.();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [brochureId, apiFetch, toastErr]);

  // Close on Esc.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCloseRef.current?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="fixed inset-0 z-[300] flex" data-testid="brochure-events-drawer">
      <div
        className="absolute inset-0 bg-black/40 z-0"
        onClick={() => onCloseRef.current?.()}
        aria-hidden="true"
      />
      <div className="relative z-10 ml-auto w-full max-w-md bg-white h-full overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              Event timeline
            </div>
            <div className="text-[14px] font-semibold text-gray-900 truncate max-w-[300px]"
                 data-testid="brochure-events-title">
              {data?.title || (loading ? "Loading…" : "Brochure")}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onCloseRef.current?.()}
            className="p-1 rounded hover:bg-gray-100"
            data-testid="brochure-events-close"
            aria-label="Close"
          >
            <XIcon size={16} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 text-[13px] p-6">
            <Loader2 className="animate-spin" size={14} /> Loading events…
          </div>
        ) : data ? (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-4 gap-2">
              {["opened", "call_clicked", "text_clicked", "credit_app_clicked"].map((t) => (
                <div key={t} className="text-center border border-gray-200 rounded-md p-2">
                  <div className="text-[10px] font-semibold uppercase text-gray-500">
                    {t === "opened" ? "Opens"
                      : t === "call_clicked" ? "Call"
                      : t === "text_clicked" ? "Text" : "Credit"}
                  </div>
                  <div className="text-lg font-bold text-gray-900 font-mono">
                    {data.counts?.[t] || 0}
                  </div>
                </div>
              ))}
            </div>

            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Rep
              </div>
              <div className="text-[13px] text-gray-900">
                {data.created_by?.name || "—"}
                {data.created_by?.email && (
                  <span className="text-gray-400 text-[11.5px]"> · {data.created_by.email}</span>
                )}
              </div>
              <div className="text-[11.5px] text-gray-500 mt-1 truncate">
                Source: {data.source_url}
              </div>
            </div>

            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Timeline ({data.events?.length || 0} events)
              </div>
              {(data.events || []).length === 0 ? (
                <div className="text-[12.5px] text-gray-500 italic border border-dashed border-gray-200 rounded-md p-4 text-center">
                  No events yet. As soon as the customer opens the link, activity shows up here.
                </div>
              ) : (
                <ul className="space-y-1.5" data-testid="brochure-events-list">
                  {data.events.map((e, i) => (
                    <li
                      key={i}
                      className="text-[12.5px] flex items-start gap-2 border-b border-gray-100 pb-1.5"
                      data-testid={`brochure-event-row-${i}`}
                    >
                      <div className={`shrink-0 w-2 h-2 rounded-full mt-1.5 ${
                        e.event_type === "opened"                ? "bg-blue-500"
                          : e.event_type === "call_clicked"      ? "bg-emerald-600"
                          : e.event_type === "text_clicked"      ? "bg-indigo-500"
                          : "bg-amber-500"
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-800">
                          {EVENT_LABELS[e.event_type] || e.event_type}
                        </div>
                        <div className="text-gray-500 text-[11.5px]">
                          {shortDate(e.occurred_at)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function MyBrochuresList({ refreshKey }) {
  const { apiFetch } = useAuth();
  const { success: toastOk, error: toastErr } = useToast();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState("self");
  const [days, setDays] = useState(0);       // 0 = all time
  const [q, setQ] = useState("");
  const [drawerId, setDrawerId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (days) params.set("days", String(days));
      if (q.trim()) params.set("q", q.trim());
      const qs = params.toString() ? `?${params.toString()}` : "";
      const r = await apiFetch(`/dealer/brochure/list${qs}`, { method: "GET" });
      const d = r?.data || r;
      setItems(Array.isArray(d.items) ? d.items : []);
      setScope(d.scope || "self");
    } catch {
      toastErr("Could not load your brochures.");
    } finally {
      setLoading(false);
    }
  }, [apiFetch, toastErr, days, q]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const copy = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toastOk("Link copied to clipboard.");
    } catch {
      toastErr("Couldn't copy — select and copy manually.");
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5"
         data-testid="brochure-list-card">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-[15px] font-semibold text-gray-900">
            {scope === "dealership" ? "Dealership brochures" : "My brochures"}
          </h3>
          <p className="text-[12px] text-gray-500 mt-0.5">
            {scope === "dealership"
              ? "Every brochure generated across your dealership. Click a row for its event timeline."
              : "Brochures you've generated. Click a row for its event timeline."}
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50"
          data-testid="brochure-list-refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b border-gray-100"
           data-testid="brochure-list-filters">
        <div className="inline-flex items-center gap-1 text-[11.5px]">
          {DAYS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setDays(opt.value)}
              className={`px-2.5 py-1 rounded border text-[11.5px] font-semibold transition-colors ${
                days === opt.value
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
              data-testid={`brochure-list-days-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px] max-w-xs ml-auto">
          <Search size={12} className="absolute left-2.5 top-2 text-gray-400" />
          <input
            className={INPUT_CLS + " pl-7 py-1.5 text-[12px]"}
            placeholder="Search vehicle or URL"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="brochure-list-search"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="absolute right-1 top-1 p-1 text-gray-400 hover:text-gray-600"
              data-testid="brochure-list-search-clear"
              aria-label="Clear search"
            >
              <XIcon size={11} />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 text-[13px] py-6">
          <Loader2 className="animate-spin" size={14} /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="text-[13px] text-gray-500 italic py-6 text-center border border-dashed border-gray-200 rounded-md"
             data-testid="brochure-list-empty">
          {days || q
            ? "No brochures match those filters."
            : "No brochures yet. Build your first one above."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="brochure-list-table">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-3">Vehicle</th>
                {scope === "dealership" && <th className="py-2 pr-3">Rep</th>}
                <th className="py-2 pr-3">Created</th>
                <th className="py-2 pr-3">Opens</th>
                <th className="py-2 pr-3">Call</th>
                <th className="py-2 pr-3">Text</th>
                <th className="py-2 pr-3">Credit app</th>
                <th className="py-2 pr-3 text-right">Link</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const link = absShareUrl(it.share_url);
                return (
                  <tr key={it.id}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setDrawerId(it.id)}
                      data-testid={`brochure-list-row-${it.token}`}>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        {it.photo ? (
                          <img src={it.photo} alt=""
                               className="w-10 h-8 object-cover rounded border border-gray-100" />
                        ) : (
                          <div className="w-10 h-8 bg-gray-100 rounded" />
                        )}
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-900 truncate max-w-[220px]">
                            {it.title || "Brochure"}
                          </div>
                          <div className="text-[11px] text-gray-400 truncate max-w-[220px]">
                            {it.source_url}
                          </div>
                        </div>
                      </div>
                    </td>
                    {scope === "dealership" && (
                      <td className="py-2 pr-3 text-gray-700">
                        {it.created_by?.name || "—"}
                      </td>
                    )}
                    <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">
                      {shortDate(it.created_at)}
                    </td>
                    <td className="py-2 pr-3 font-mono">
                      <span data-testid={`brochure-metric-opens-${it.token}`}>
                        {it.engagement?.opened || 0}
                      </span>
                    </td>
                    <td className="py-2 pr-3 font-mono">{it.engagement?.call_clicks || 0}</td>
                    <td className="py-2 pr-3 font-mono">{it.engagement?.text_clicks || 0}</td>
                    <td className="py-2 pr-3 font-mono">{it.engagement?.credit_clicks || 0}</td>
                    <td className="py-2 pr-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1 justify-end">
                        <button
                          type="button"
                          onClick={() => copy(link)}
                          className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50"
                          data-testid={`brochure-list-copy-${it.token}`}
                          title="Copy link"
                        >
                          <Copy size={11} />
                        </button>
                        <a
                          href={link}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50"
                          data-testid={`brochure-list-open-${it.token}`}
                          title="Open brochure"
                        >
                          <Eye size={11} />
                        </a>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {drawerId && (
        <BrochureEventsDrawer
          brochureId={drawerId}
          onClose={() => setDrawerId(null)}
        />
      )}
    </div>
  );
}

// ─────────────────── Leaderboard (dealer admin+) ───────────────────

function StatChip({ label, value, testid }) {
  return (
    <div className="border border-gray-200 rounded-md px-3 py-2 min-w-[92px]" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div className="text-[18px] font-bold text-gray-900 font-mono">{value}</div>
    </div>
  );
}

function BrochureLeaderboardCard() {
  const { apiFetch } = useAuth();
  const { error: toastErr } = useToast();
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/dealer/brochure/leaderboard?days=${days}`,
        { method: "GET" });
      setData(r?.data || r);
    } catch (e) {
      const status = e?.response?.status;
      if (status !== 403) {
        toastErr(e?.response?.data?.detail || "Could not load leaderboard.");
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, days, toastErr]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-5 flex items-center gap-2 text-gray-500 text-[13px]"
           data-testid="brochure-leaderboard-loading">
        <Loader2 className="animate-spin" size={14} /> Loading leaderboard…
      </div>
    );
  }
  if (!data) return null;

  const rows = data.leaderboard || [];
  const totals = data.totals || {};

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5"
         data-testid="brochure-leaderboard-card">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-4">
        <div>
          <h3 className="text-[15px] font-semibold text-gray-900 flex items-center gap-2">
            <Trophy size={16} className="text-amber-500" /> Rep leaderboard
          </h3>
          <p className="text-[12px] text-gray-500 mt-0.5">
            How each rep is using Brochure Builder this window.
          </p>
        </div>
        <div className="inline-flex items-center gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded border text-[11.5px] font-semibold transition-colors ${
                days === d
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
              data-testid={`brochure-leaderboard-days-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <StatChip label="Brochures" value={totals.brochures || 0}
                  testid="brochure-leaderboard-total-brochures" />
        <StatChip label="Active reps" value={totals.active_reps || 0}
                  testid="brochure-leaderboard-total-reps" />
        <StatChip label="Opens" value={totals.opens || 0}
                  testid="brochure-leaderboard-total-opens" />
        <StatChip label="Calls" value={totals.calls || 0}
                  testid="brochure-leaderboard-total-calls" />
        <StatChip label="Texts" value={totals.texts || 0}
                  testid="brochure-leaderboard-total-texts" />
        <StatChip label="Credit app" value={totals.credit_clicks || 0}
                  testid="brochure-leaderboard-total-credit" />
      </div>

      {rows.length === 0 ? (
        <div className="text-[13px] text-gray-500 italic py-6 text-center border border-dashed border-gray-200 rounded-md"
             data-testid="brochure-leaderboard-empty">
          No brochures created in this window yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="brochure-leaderboard-table">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-3">Rep</th>
                <th className="py-2 pr-3 text-right">Brochures</th>
                <th className="py-2 pr-3 text-right">Opens</th>
                <th className="py-2 pr-3 text-right">Call</th>
                <th className="py-2 pr-3 text-right">Text</th>
                <th className="py-2 pr-3 text-right">Credit app</th>
                <th className="py-2 pr-3 text-right">Opens/broc</th>
                <th className="py-2 pr-3">Last brochure</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.user_id || i}
                    className="border-b border-gray-100 hover:bg-gray-50"
                    data-testid={`brochure-leaderboard-row-${i}`}>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-2">
                      <span className={`shrink-0 grid place-items-center w-6 h-6 rounded-full text-[11px] font-bold ${
                        i === 0 ? "bg-amber-100 text-amber-800"
                          : i === 1 ? "bg-gray-100 text-gray-700"
                          : i === 2 ? "bg-orange-50 text-orange-800"
                          : "bg-gray-50 text-gray-500"
                      }`}>
                        {i + 1}
                      </span>
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 truncate max-w-[180px]">
                          {r.name}
                        </div>
                        <div className="text-[11px] text-gray-400 truncate max-w-[180px]">
                          {r.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 pr-3 font-mono text-right">{r.brochures}</td>
                  <td className="py-2 pr-3 font-mono text-right">{r.opens}</td>
                  <td className="py-2 pr-3 font-mono text-right">{r.calls}</td>
                  <td className="py-2 pr-3 font-mono text-right">{r.texts}</td>
                  <td className="py-2 pr-3 font-mono text-right">{r.credit_clicks}</td>
                  <td className="py-2 pr-3 font-mono text-right text-gray-500">
                    {r.engagement_rate}%
                  </td>
                  <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">
                    {shortDate(r.last_brochure_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export { BrochureLeaderboardCard };

// ─────────────────── Page shell ───────────────────

export default function BrochureBuilderPage() {
  const { apiFetch, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [listRefreshKey, setListRefreshKey] = useState(0);

  const role = String(user?.role || "").toUpperCase();
  const canSeeLeaderboard =
    role === "DEALER_ADMIN" || role === "MASTER_ADMIN" || role === "SUB_MASTER";

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setStatusError(null);
    try {
      const r = await apiFetch("/dealer/brochure/status", { method: "GET" });
      setStatus(r?.data || r);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        setStatusError("Brochure Builder isn't enabled for your dealership yet.");
      } else {
        setStatusError(e?.response?.data?.detail || "Couldn't load Brochure Builder.");
      }
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-gray-500 text-[13px]"
           data-testid="brochure-builder-loading">
        <Loader2 className="animate-spin" size={16} /> Loading Brochure Builder…
      </div>
    );
  }

  if (statusError) {
    return (
      <div className="p-6" data-testid="brochure-builder-blocked">
        <div className="max-w-lg bg-amber-50 border border-amber-200 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-700 mt-0.5" />
            <div>
              <div className="text-[15px] font-semibold text-amber-900">
                Brochure Builder unavailable
              </div>
              <div className="text-[13px] text-amber-800 mt-1">{statusError}</div>
              <button
                type="button"
                onClick={loadStatus}
                className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold px-3 py-1.5 rounded border border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
                data-testid="brochure-builder-retry"
              >
                <RefreshCw size={12} /> Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5" data-testid="brochure-builder-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Brochure Builder</h1>
        <p className="text-[13px] text-gray-500 mt-1 max-w-2xl">
          Turn any listing on <strong>{status?.dealer_name || "your dealership"}</strong>&apos;s
          site into a mobile-friendly brochure your customer can open in one tap.
        </p>
      </div>

      <RepProfileCard
        status={status}
        onSaved={(updated) => {
          setStatus((prev) => (prev ? { ...prev, rep: { ...(prev.rep || {}), ...updated } } : prev));
        }}
      />

      <GeneratorCard
        status={status}
        onCreated={() => setListRefreshKey((k) => k + 1)}
      />

      {canSeeLeaderboard && <BrochureLeaderboardCard />}

      <MyBrochuresList refreshKey={listRefreshKey} />
    </div>
  );
}
